import React, { useState, useEffect } from 'react';
import {
  Chip,
  Spinner,
  Button
} from "@heroui/react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody
} from "@heroui/modal";
import { useCreatePreview } from '../hooks/useCreatePreview';
import ZeroBounceSDK from '@zerobounce/zero-bounce-sdk';

const CampaignPreview = ({
    cancelPreview,
    cancelForm,
    mailer_content,
    user,
    typos,
    subject,
    tone,
    prompt_inject,
    variability,
    vanity_url,
    toRecipients,
    ccRecipients,
    bccRecipients,
    stompClient,
    footer,
    campaign,
    setBanner,
    language
}) => {
  const [loading, setLoading] = useState(true);
  // We set previewSubject and body when we poll and finally get the preview data
  const [body, setBody] = useState();
  const [previewSubject, setPreviewSubject] = useState();
  const [toValidations, setToValidations] = useState();
  const [ccValidations, setCcValidations] = useState();
  const [bccValidations, setBccValidations] = useState();
  const previewId = useCreatePreview(subject, mailer_content, tone, variability, campaign, prompt_inject, language);

  useEffect(() => {
    const validateEmails = async () => {
      const zsdk = new ZeroBounceSDK();
      zsdk.init('e2a89686b39d4d4ab82d99a69db516fd');
      if (toRecipients && toRecipients.length > 0) {
        const toRecs = toRecipients.map(rec => {return {email_address: rec}});
        const toData = await zsdk.validateBatch(toRecs);
        setToValidations(toData['email_batch']);
      }

      if (ccRecipients && ccRecipients.length > 0) {
        const ccRecs = ccRecipients.map(rec => {return {email_address: rec}});
        const ccData = await zsdk.validateBatch(ccRecs);
        setCcValidations(ccData['email_batch']);
      }

      if (bccRecipients && bccRecipients.length > 0) {
        const bccRecs = bccRecipients.map(rec => {return {email_address: rec}});
        const bccData = await zsdk.validateBatch(bccRecs);
        setBccValidations(bccData['email_batch']);
      }
      // Example response:
      // {"email_batch":[{"address":"wedge.martin@gmail.com","status":"valid","sub_status":"","free_email":true,"did_you_mean":null,"account":"wedgemartin","domain":"gmail.com","domain_age_days":"10875","smtp_provider":"google","mx_found":"true","mx_record":"alt1.gmail-smtp-in.l.google.com","firstname":"martin","lastname":"wedge","gender":"male","country":null,"region":null,"city":null,"zipcode":null,"processed_at":"2025-05-22 10:16:48.352"}],"errors":[]}
      // With bad emails
      //  TO VALIDATIONS: {"email_batch":[{"address":"wedge.martin@gmail.com","status":"valid","sub_status":"","free_email":true,"did_you_mean":null,"account":"wedgemartin","domain":"gmail.com","domain_age_days":"10875","smtp_provider":"google","mx_found":"true","mx_record":"alt1.gmail-smtp-in.l.google.com","firstname":"martin","lastname":"wedge","gender":"male","country":null,"region":null,"city":null,"zipcode":null,"processed_at":"2025-05-22 10:18:03.035"},{"address":"foofyay@flarfaenmarf.org","status":"invalid","sub_status":"no_dns_entries","free_email":false,"did_you_mean":null,"account":"foofyay","domain":"flarfaenmarf.org","domain_age_days":null,"smtp_provider":"","mx_found":"false","mx_record":"","firstname":null,"lastname":null,"gender":null,"country":null,"region":null,"city":null,"zipcode":null,"processed_at":"2025-05-22 10:18:03.036"},{"address":"asdf@asdfasdf.asdfasdfasdfads","status":"invalid","sub_status":"failed_syntax_check","free_email":false,"did_you_mean":null,"account":"","domain":"","domain_age_days":"","smtp_provider":"","mx_found":"false","mx_record":null,"firstname":"","lastname":"","gender":"","country":null,"region":null,"city":null,"zipcode":null,"processed_at":"2025-05-22 10:18:03.035"}],"errors":[]}
    };
    validateEmails();
    stompClient?.subscribe(`/topic/users/${user?._id || user?.id}_campaigns`, (message) => handleStompMessage(message));
    // eslint-disable-next-line
  }, [previewId, campaign, mailer_content, subject, tone, variability]);

  const handleStompMessage = async (m) => {
    const message = JSON.parse(m.body);
    console.log("[handleStompMessage]: " + JSON.stringify(message));
    if (message['preview_update'] && previewId && message['preview_update']['campaign_preview_id'] === previewId) {
      if (message['preview_update']['mailer_content']) {
        setBody(message['preview_update']['mailer_content']);
        if (previewSubject) {
          setLoading(false);
        }
      } else if (message['preview_update']['subject']) {
        setPreviewSubject(message['preview_update']['subject']);
        if (body) {
          setLoading(false);
        }
      }
    }
  };

  return (
    <Modal placement='top' size='xl' scrollBehavior='inside' isOpen={true} isDismissable={true}
           isKeyboardDismissDisabled={false} onClose={cancelPreview}>
      <ModalContent>
        <ModalHeader>
          <div style={{display: 'flex', flexDirection: 'column'}}>
            <span style={{fontSize: '16px', fontWeight: 'bold'}}>
              Preview of a sample email for:
              <br />
              https://app.ActivistMailer.com/{vanity_url || <i>Not specified yet</i>}
            </span>
          </div>
        </ModalHeader>
        <ModalBody>
          { (loading && (!body && !previewSubject))
            ? <div>
                <div style={{width: '480px', display: 'inline-block'}}>
                  <span style={{paddingLeft: '6px', fontSize: '14px'}}>
                    <i> Processing. This may take a few moments...</i>
                  </span>
                </div>
              </div>
            :
              ''
          }
          <p />
          <div className='helper'>
            <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
              <b>To</b>: { toRecipients.map(r => <Chip color={toValidations && toValidations.filter(to => to['address'] === r)?.[0]?.['status'] === 'valid' ? 'success' : 'danger'} variant="bordered">{r}</Chip>) }
            </div>

            <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
              <b>Cc</b>: { ccRecipients.map(r => <Chip color={ccValidations && ccValidations.filter(cc => cc['address'] === r)?.[0]?.['status'] === 'valid' ? 'success' : 'danger'} variant="bordered">{r}</Chip>) }
            </div>

            <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
              <b>Bcc</b>: { bccRecipients.map(r => <Chip color={bccValidations && bccValidations.filter(bcc => bcc['address'] === r)?.[0]?.['status'] === 'valid' ? 'success' : 'danger'} variant="bordered">{r}</Chip>) }
            </div>

          </div>

          <div className='helper'>
            <span><b>Subject</b>: {(previewSubject && previewSubject !== '') ? <pre style={{whiteSpace: 'pre-wrap'}}>{previewSubject}</pre> : <Spinner />}</span>
          </div>

          <div style={{width: '492px', fontSize: 'medium'}}>
            <b>Body</b>: {((body && body !== '') || variability === 0) ? <pre style={{whiteSpace: 'pre-wrap'}}>{variability === 0 ? mailer_content : body}</pre> : <Spinner />}
          </div>
          { (footer && body && body !== '') && <span style={{fontSize: '14px'}}>Mailed with the help of ActivistMailer.com</span> }
          <div style={{margin: '6px'}}>
            <Button style={{marginRight: '8px'}} onPress={cancelPreview} type='submit'>OK</Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default CampaignPreview;
